import { Command, CommandInput, CommandItem, CommandList } from '@shared/components/ui/command';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { DropdownItem } from './types';

interface WorkspaceDropdownMenuItemsProps {
  items: DropdownItem[];
}

export function WorkspaceDropdownMenuItems({ items }: WorkspaceDropdownMenuItemsProps) {
  const { t } = useTranslation('menu');

  return items.map((item, index) => {
    if (item.type === 'separator') {
      return <DropdownMenuSeparator key={item.id ?? `separator-${index}`} />;
    }

    if (item.type === 'label') {
      return (
        <DropdownMenuLabel key={index} className="cursor-default select-none px-2 py-1 opacity-70">
          {item.label}
        </DropdownMenuLabel>
      );
    }

    if (item.children) {
      return (
        <DropdownMenuSub key={index}>
          <DropdownMenuSubTrigger className="!text-text">
            {item.icon}
            <span className="!text-text">{item.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <Command className="bg-body-bg [&_[cmdk-input-wrapper]>svg]:opacity-20">
              {item.childrenSearchable !== false && (
                <CommandInput placeholder={t('search_printer')} className="placeholder:opacity-20" />
              )}
              <CommandList>
                {item.children.map((child, childIndex) =>
                  child.type === 'label' ? (
                    <DropdownMenuLabel key={childIndex} className="cursor-default pb-1 pt-1">
                      {child.label}
                    </DropdownMenuLabel>
                  ) : (
                    <CommandItem
                      key={childIndex}
                      onSelect={() => child.onClick?.()}
                      className={child.onClick ? 'cursor-pointer hover:bg-muted' : 'cursor-default'}
                      disabled={child.disabled}
                    >
                      {child.label}
                    </CommandItem>
                  ),
                )}
              </CommandList>
            </Command>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    if (item.path) {
      return (
        <DropdownMenuItem key={index} onSelect={item.onClick} asChild>
          <Link to={item.path} className="flex w-full items-center gap-2">
            {item.icon}
            <span className="!text-text w-full">{item.label}</span>
          </Link>
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem
        key={index}
        onSelect={item.onClick}
        className={!item.icon ? 'p-0 focus:bg-transparent' : undefined}
      >
        {item.icon ? (
          <div className="flex w-full items-center gap-2">
            {item.icon}
            <span className="!text-text w-full">{item.label}</span>
          </div>
        ) : (
          item.label
        )}
      </DropdownMenuItem>
    );
  });
}
